import { common } from '@mui/material/colors'
import { alpha, createTheme, darken } from '@mui/material/styles'
import { palette } from './styles/colors'

export const blackMFC = 'rgb(67, 67, 67)'

export const greenMFC = 'rgb(56, 142, 60)'

export const activeGreen = 'green'
export const inactiveGrey = 'grey'

export const CAPTION_HEIGHT_RATIO = 0.18

export const COMPOSE_ICONS_SPACING = 1
export const PADDING_GRID_BLEED = 4 // 2

export const CHAT_HEAD_WIDTH = 48

export const APP_FONT = `'Montserrat', 'Helvetica', 'Arial', sans-serif`

export const FONT_SIZES = {
    body1: '0.9285714285714286rem',
    body2: '0.8125rem',
    body3: '0.74rem',
    caption: '0.6964285714285714rem',
    h1: '5.571428571428571rem',
    h2: '3.482142857142857rem',
    h3: '2.7857142857142856rem',
    h4: '1.9732142857142858rem',
    h5: '1.3928571428571428rem',
    h6: '1.1607142857142858rem',
    subtitle1: '0.9285714285714286rem',
    subtitle2: '0.8125rem',
    overline: '0.6964285714285714rem',
}

/**
 * Commonly found zIndex values across app.mfc
 */
export const PAPER_HEIGHTS = {
    // sorted by z-index elevation
    // max z-index is a signed int32: 2147483647
    highest: /*             */ 2147483647,
    tooltip: /*             */ 2147483603,
    tip: /*                 */ 2147483602,
    videoForefront: /*      */ 2147483601,
    snapStream: /*          */ 2147483600,
    dialogConfirmation: /*  */ 999999999 + 3,
    footer: /*              */ '999999999 !important' as any,
    dragSidebar: /*         */ 1000000000,
    dialog: /*              */ 999999999,
    drawer: /*              */ 999999995,
    quickReply: /*          */ 999999994,
    // floatingPreview: /*     */ 999999991,
    floater: /*             */ 999999990,
    emoticon: /*            */ 99999999,
    performance: /*         */ 9999999,
    videoPreview: /*        */ 9999999,
    videoBlackout: /*       */ 9999998,
    closeButton: /*         */ 1301,
    floatingActionBar: /*   */ 1300,
    floatingChat: /*        */ 1300,
    roomCover: /*           */ 1299,
    videoStreamContainer: /**/ 1298,
    fullWindowVideo: /*     */ 1000,
    base: /*                */ 1,
    zero: /*                */ 0,
    low: /*                 */ -1,
} as const

export const COMPONENT_HEIGHTS = {
    actionsBar: 40,
    availableSpaceControlBar: 40,
    availableSpaceMinimum: 80,
    chatArchivesHeader: 48,
    chatHeader: 48,
    chatHeaderOffline: 70,
    chatHeaderOfflineDesktop: 96,
    compose: 40,
    detailsTileHeader: 52,
    detailsTileHeadline: 23,
    detailsTileTopic: 68,
    detailsTileInfo: 26,
    drawerTitle: 48,
    headerCompact: 24,
    headerDesktop: 40,
    inputSearch: 34,
    nav: 62,
    navBottom: 55,
    navExpanded: 52,
    navSmall: 50,
    tabs: 48,
} as const

export const COMPONENT_WIDTHS = {
    drawer: 300,
    leftNav: 56,
    leftNavExpanded: 226,
    sidebar: 180,
    dragSidebar: 62,
    tagsPanel: 200,
    tagsPanelSmall: 160,
    detailsTile: 330,
} as const

const getComputedPropertyValue = (property: string) => () =>
    typeof window !== 'undefined'
        ? Number(
              window
                  .getComputedStyle(document.documentElement)
                  .getPropertyValue(property) // unfortunately querying the env var directly isnt reliable across browsers
                  .split('px')[0],
          ) || 0
        : 0

// JS access to `safe-area-inset-bottom` and `safe-area-inset-left` values -
// this is the extra space that mobile browsers add to make space for camera
// or speaker grills interrupting the screen, so it has to be accounted for in
// modals and anything else that needs to have an accurate height from bottom
export const getBrowserBottomInset = getComputedPropertyValue('--saib')

export const FOOTER_HEIGHT_MOBILE = 60

export const PINNED_ROOM_CHAT_MIN_WIDTH = 250
export const PINNED_ROOM_MAX_HEIGHT = 650
export const PINNED_ROOM_MIN_WIDTH_FOR_CHAT = 540

export function getFill<Type>(target: Type, ueopt: Type): string {
    return ueopt === target ? palette.checkboxOptionColor : inactiveGrey
}

// USE THE `no-scrollbars` CSS CLASS INSTEAD
// className="no-scrollbars"
// export const NO_SCROLLBAR_STYLES = {
//     overflowX: 'hidden',
//     scrollbarWidth: 'none',
//     scrollbarColor: 'transparent transparent',
//     '&::WebkitScrollbar': {
//         width: 0,
//         height: 0,
//     },
//     '&::WebkitScrollbarTrack': {
//         background: 'transparent',
//     },
//     '&::WebkitScrollbarThumb': {
//         backgroundColor: 'transparent',
//     },
//     '&::-webkit-scrollbar': {
//         width: 0,
//         height: 0,
//     },
//     '&::-webkit-scrollbar-track': {
//         background: 'transparent',
//     },
//     '&::-webkit-scrollbar-thumb': {
//         backgroundColor: 'transparent',
//     },
// } as const

/**
 * Material UI v6 Theme
 * @param darkMode
 * @returns
 */
const theme = (darkMode: boolean) => {
    return createTheme({
        palette: {
            mode: darkMode ? 'dark' : 'light',
            primary: {
                main: greenMFC,
                light: alpha(greenMFC, 0.3),
                dark: darken(greenMFC, 0.3),
                contrastText: common.white,
            },
        },
        components: {
            // pass all mui typography components base font - do not remove
            MuiTypography: {
                defaultProps: {
                    fontFamily: APP_FONT,
                },
            },
            // end global font setup for theme
            // MuiButton: {
            //     styleOverrides: {
            //         root: {
            //             border: `1px solid ${palette.buttonBorder}`,
            //             styleOverrides: {
            //                 root: {
            //                     '& :focused': {
            //                         color: darkMode
            //                             ? lighten(greenMFC, 0.3)
            //                             : darken(greenMFC, 0.3),
            //                     },
            //                 },
            //             },
            //             fontFamily: APP_FONT,
            //             textTransform: 'capitalize',
            //         },
            //         contained: {
            //             transition: 'color 0.16s ease',
            //             '&:hover': {
            //                 color: common.white,
            //             },
            //         },
            //         outlined: {
            //             padding: '0.5rem',
            //         },
            //     },
            // },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        '&:hover': {
                            backgroundColor: palette.radioHover,
                        },
                    },
                },
            },
            MuiRadio: {
                styleOverrides: {
                    root: {
                        '&:hover': {
                            backgroundColor: palette.radioHover,
                        },
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        '&.Mui-selected': {
                            backgroundColor: palette.rowSelectedBackground,
                        },
                    },
                },
            },
        },
    })
}

export default theme
